import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, Radio, KeyRound } from "lucide-react";

const CARD = "rounded-2xl bg-white p-5 shadow-[0_10px_28px_-14px_rgba(10,35,74,0.22),0_0_0_1px_rgba(10,35,74,0.05)]";
const INPUT = "h-11 bg-white border-neutral-200 text-[#0A234A] rounded-xl focus-visible:border-[#FF5722] focus-visible:ring-2 focus-visible:ring-[#FF5722]/25";

export default function IntegrationsPanel({ compact = false }) {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => { api.get("/integrations").then((r) => setStatus(r.data)).catch(() => setStatus(null)); }, []);
  useEffect(() => { load(); }, [load]);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setSaving(true); setMsg("");
    const payload = {};
    for (const [k, v] of Object.entries(form)) if (String(v).trim()) payload[k] = v;
    try { const r = await api.post("/integrations", payload); setForm({}); load(); setMsg(`Saved. Active fleet source: ${r.data.telematics_source}.`); }
    catch (e) { setMsg(e.response?.data?.detail || "Save failed."); }
    setSaving(false);
  };
  if (!status) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#FF5722]" /></div>;
  const Pill = ({ ok, children }) => <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${ok ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-500"}`}>{ok && <CheckCircle2 className="h-3 w-3" />}{children}</span>;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0A234A]/5 px-3 py-1 text-xs font-bold text-[#0A234A]"><Radio className="h-3.5 w-3.5" /> Active fleet source: {status.telematics_source}</span>
        <Pill ok={status.samsara.configured}>Samsara</Pill>
        <Pill ok={status.geotab.configured}>Geotab</Pill>
        <Pill ok={status.alberta_511.configured}>511 Alberta</Pill>
        <Pill ok={status.nsw_traffic?.configured}>NSW Live Traffic</Pill>
        <Pill ok={status.qldtraffic?.configured}>QLDTraffic</Pill>
      </div>

      <div className={`grid gap-5 ${compact ? "" : "lg:grid-cols-2"}`}>
        <div className={CARD}>
          <p className="mb-1 font-display font-bold text-[#0A234A]">Samsara</p>
          <p className="mb-3 text-xs text-neutral-500">Dashboard → Settings → API Tokens. {status.samsara.configured ? `Saved: ${status.samsara.token_masked}` : "Not configured."}</p>
          <Label className="text-neutral-600 text-xs">API token</Label>
          <Input data-testid="int-samsara-token" type="password" value={form.SAMSARA_API_TOKEN || ""} onChange={upd("SAMSARA_API_TOKEN")} placeholder="samsara_api_…" className={INPUT} />
        </div>

        <div className={CARD}>
          <p className="mb-1 font-display font-bold text-[#0A234A]">Geotab (MyGeotab)</p>
          <p className="mb-3 text-xs text-neutral-500">Your MyGeotab login (use a read-only API user). {status.geotab.configured ? `Saved: ${status.geotab.username} / ${status.geotab.database}` : "Not configured."}</p>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-neutral-600 text-xs">Username</Label><Input data-testid="int-geotab-user" value={form.GEOTAB_USERNAME || ""} onChange={upd("GEOTAB_USERNAME")} placeholder="you@company.com" className={INPUT} /></div>
            <div><Label className="text-neutral-600 text-xs">Password</Label><Input data-testid="int-geotab-pass" type="password" value={form.GEOTAB_PASSWORD || ""} onChange={upd("GEOTAB_PASSWORD")} placeholder="••••••••" className={INPUT} /></div>
            <div><Label className="text-neutral-600 text-xs">Database</Label><Input data-testid="int-geotab-db" value={form.GEOTAB_DATABASE || ""} onChange={upd("GEOTAB_DATABASE")} placeholder="your_company" className={INPUT} /></div>
            <div><Label className="text-neutral-600 text-xs">Server</Label><Input value={form.GEOTAB_SERVER || ""} onChange={upd("GEOTAB_SERVER")} placeholder="https://my.geotab.com" className={INPUT} /></div>
          </div>
        </div>

        <div className={CARD}>
          <p className="mb-1 font-display font-bold text-[#0A234A]">511 Alberta (road events)</p>
          <p className="mb-3 text-xs text-neutral-500">Public road-events key from 511.alberta.ca/developers/doc. {status.alberta_511.configured ? `Saved: ${status.alberta_511.key_masked}` : "Using live DriveBC Open511 fallback."}</p>
          <Label className="text-neutral-600 text-xs">API key</Label>
          <Input data-testid="int-511-key" type="password" value={form.ALBERTA_511_KEY || ""} onChange={upd("ALBERTA_511_KEY")} placeholder="alberta-511-key" className={INPUT} />
        </div>

        <div className={CARD}>
          <p className="mb-1 font-display font-bold text-[#0A234A]">NSW Live Traffic (AU road events)</p>
          <p className="mb-3 text-xs text-neutral-500">Free key from opendata.transport.nsw.gov.au (add the "Live Traffic Hazards" API). {status.nsw_traffic?.configured ? `Saved: ${status.nsw_traffic.key_masked}` : "Not configured — Australia road layer needs this."}</p>
          <Label className="text-neutral-600 text-xs">API key</Label>
          <Input data-testid="int-nsw-key" type="password" value={form.NSW_TRAFFIC_KEY || ""} onChange={upd("NSW_TRAFFIC_KEY")} placeholder="nsw-opendata-apikey" className={INPUT} />
        </div>

        <div className={CARD}>
          <p className="mb-1 font-display font-bold text-[#0A234A]">QLDTraffic (AU road events, optional)</p>
          <p className="mb-3 text-xs text-neutral-500">Optional own key from qldtraffic.qld.gov.au. {status.qldtraffic?.configured ? `Saved: ${status.qldtraffic.key_masked}` : "Falls back to the shared public key (rate-limited)."}</p>
          <Label className="text-neutral-600 text-xs">API key</Label>
          <Input data-testid="int-qld-key" type="password" value={form.QLDTRAFFIC_KEY || ""} onChange={upd("QLDTRAFFIC_KEY")} placeholder="qldtraffic-apikey" className={INPUT} />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button data-testid="int-save" onClick={save} disabled={saving} className="rounded-xl bg-[#FF5722] font-bold text-white hover:bg-[#F4511E]">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />} Save integrations
        </Button>
        {msg && <span data-testid="int-msg" className="text-sm text-neutral-600">{msg}</span>}
      </div>
      <p className="text-xs text-neutral-400">Keys are stored server-side and override backend config. Only filled fields are saved; leave blank to keep the current value.</p>
    </div>
  );
}
